import { logger } from '../lib/logger';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Users, Menu, LogOut, Home, ClipboardList, BarChart3, Newspaper, Bot,
  MessageCircle, UserRound, ExternalLink, TrendingUp, ThumbsDown, PlayCircle,
  CheckCircle2, AlertTriangle, Globe, Image as ImageIcon, RefreshCw, Trash2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import AdminNav from '../components/AdminNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const mdComponents = {
  p: (props) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 space-y-1 mb-2" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 space-y-1 mb-2" {...props} />,
  strong: (props) => <strong className="text-white font-bold" {...props} />,
  a: (props) => <a className="text-yellow-400 underline underline-offset-2" {...props} />,
  table: (props) => <div className="overflow-x-auto my-2"><table className="w-full text-xs border border-gray-700 rounded" {...props} /></div>,
  thead: (props) => <thead className="bg-gray-800 text-yellow-400" {...props} />,
  th: (props) => <th className="text-left px-2 py-1 border-b border-gray-700 font-bold" {...props} />,
  td: (props) => <td className="px-2 py-1 border-b border-gray-800 text-gray-300" {...props} />,
  code: (props) => <code className="bg-gray-800 text-yellow-300 px-1 py-0.5 rounded text-xs" {...props} />,
};

const formatWA = (d) => d?.length >= 10 ? `(${d.slice(0, 2)}) ${d.slice(2, -4)}-${d.slice(-4)}` : d;

const Card = ({ titulo, valor, cor }) => (
  <div className="glass-dark border border-white/10 rounded-xl p-4" data-testid={`card-${titulo}`}>
    <div className="text-2xl font-black" style={{ color: cor || '#facc15' }}>{valor}</div>
    <div className="text-xs text-gray-400 mt-1">{titulo}</div>
  </div>
);

const Barra = ({ label, valor, max, cor = 'bg-yellow-500' }) => (
  <div className="mb-2">
    <div className="flex justify-between text-xs mb-1"><span className="text-gray-300 truncate pr-2">{label}</span><span className="text-gray-500">{valor}</span></div>
    <div className="h-2 bg-gray-800 rounded overflow-hidden"><div className={`h-2 ${cor} rounded`} style={{ width: `${max ? Math.round((valor / max) * 100) : 0}%` }} /></div>
  </div>
);

const AgenteAnalyticsPanel = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [unresolved, setUnresolved] = useState([]);
  const [reg, setReg] = useState(null);
  const [regLoading, setRegLoading] = useState(false);

  const carregar = async () => {
    try {
      const [a, u] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/agente/analytics`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/agente/unresolved`, { withCredentials: true }),
      ]);
      setData(a.data); setUnresolved(u.data);
    } catch (e) { logger.error('Erro analytics agente:', e); }
  };
  useEffect(() => { carregar(); }, []);

  const rodarRegressao = async () => {
    setRegLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/agente/regression`, {}, { withCredentials: true });
      setReg(r.data);
      toast({ title: `Regressão: ${r.data.aprovados}/${r.data.total} aprovados` });
    } catch (e) {
      toast({ title: 'Falha ao rodar regressão', variant: 'destructive' });
    } finally { setRegLoading(false); }
  };

  const resolver = async (id) => {
    try {
      await axios.post(`${BACKEND_URL}/api/agente/unresolved/${id}/resolver`, {}, { withCredentials: true });
      setUnresolved((u) => u.filter((x) => x.id !== id));
    } catch (e) { logger.error(e); }
  };

  if (!data) return <p className="text-gray-500 text-sm py-10">Carregando analytics...</p>;
  const topico = data.por_topico || {}; const maxTop = Math.max(1, ...Object.values(topico));
  const docs = data.docs_recuperados || {}; const maxDoc = Math.max(1, ...Object.values(docs));
  const ev = data.por_evidencia || {};

  return (
    <div className="overflow-y-auto pr-1" data-testid="agente-analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <Card titulo="Perguntas (30d)" valor={data.total_perguntas} />
        <Card titulo="Úteis 👍" valor={data.feedback?.util ?? 0} cor="#4ade80" />
        <Card titulo="Não úteis 👎" valor={data.feedback?.nao_util ?? 0} cor="#f87171" />
        <Card titulo="Uso de web" valor={data.uso_web} cor="#60a5fa" />
        <Card titulo="Sem resposta" valor={data.sem_resposta_abertas} cor="#fbbf24" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="glass-dark border border-white/10 rounded-xl p-4">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-yellow-400" /> Assuntos mais perguntados</h3>
          {Object.entries(topico).map(([k, v]) => <Barra key={k} label={k} valor={v} max={maxTop} />)}
        </div>
        <div className="glass-dark border border-white/10 rounded-xl p-4">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Bot className="w-4 h-4 text-yellow-400" /> Documentos mais recuperados</h3>
          {Object.entries(docs).slice(0, 8).map(([k, v]) => <Barra key={k} label={k} valor={v} max={maxDoc} cor="bg-cyan-500" />)}
          <div className="mt-3 text-xs text-gray-400 flex gap-4">
            <span className="inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Visão: {data.uso_visao}</span>
            <span>Confirmadas: {ev.OFFICIAL_CONFIRMED || 0}</span>
            <span>Insuficientes: {ev.INSUFFICIENT_EVIDENCE || 0}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        <div className="glass-dark border border-white/10 rounded-xl p-4">
          <h3 className="text-white font-bold mb-3">Perguntas mais frequentes</h3>
          <div className="space-y-1.5">
            {(data.top_perguntas || []).map((p, i) => (
              <div key={i} className="flex justify-between text-xs text-gray-300 border-b border-gray-800/50 pb-1">
                <span className="truncate pr-2">{p.pergunta}</span><span className="text-yellow-400 font-bold">{p.total}</span>
              </div>
            ))}
            {(data.top_perguntas || []).length === 0 && <p className="text-gray-500 text-sm">Sem dados ainda.</p>}
          </div>
        </div>
        <div className="glass-dark border border-white/10 rounded-xl p-4">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2"><ThumbsDown className="w-4 h-4 text-red-400" /> Respostas com 👎</h3>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {(data.feedback_negativos || []).map((f, i) => (
              <div key={i} className="text-xs bg-black/30 border border-gray-800 rounded p-2">
                <div className="text-gray-200 font-medium">{f.pergunta || '(sem texto)'}</div>
                <div className="text-gray-500 mt-0.5">{f.topic} · {f.evidence_status}{f.comentario ? ` · "${f.comentario}"` : ''}</div>
              </div>
            ))}
            {(data.feedback_negativos || []).length === 0 && <p className="text-gray-500 text-sm">Nenhum 👎 registrado.</p>}
          </div>
        </div>
      </div>

      <div className="glass-dark border border-white/10 rounded-xl p-4 mb-5" data-testid="unresolved-panel">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-400" /> Perguntas sem resposta ({unresolved.length})</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {unresolved.map((u) => (
            <div key={u.id} className="flex items-start justify-between gap-3 text-xs bg-black/30 border border-gray-800 rounded p-2.5" data-testid={`unresolved-${u.id}`}>
              <div className="min-w-0">
                <div className="text-gray-200 font-medium">{u.question}</div>
                <div className="text-gray-500 mt-0.5">{u.category} · {u.reason === 'negative_feedback' ? '👎 feedback' : 'sem evidência'} · {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : ''}</div>
              </div>
              <button onClick={() => resolver(u.id)} data-testid={`resolver-${u.id}`} className="flex-shrink-0 inline-flex items-center gap-1 text-green-400 hover:text-green-300 text-xs font-bold">
                <CheckCircle2 className="w-4 h-4" /> Resolver
              </button>
            </div>
          ))}
          {unresolved.length === 0 && <p className="text-gray-500 text-sm">Nenhuma pergunta pendente. 🎉</p>}
        </div>
      </div>

      <div className="glass-dark border border-white/10 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold flex items-center gap-2"><PlayCircle className="w-4 h-4 text-yellow-400" /> Testes de regressão (recuperação)</h3>
          <button onClick={rodarRegressao} disabled={regLoading} data-testid="rodar-regressao-btn" className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm px-4 py-2 rounded-lg disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${regLoading ? 'animate-spin' : ''}`} /> {regLoading ? 'Rodando...' : 'Rodar testes'}
          </button>
        </div>
        {reg && (
          <div data-testid="regressao-resultado">
            <div className={`text-sm font-bold mb-2 ${reg.aprovados === reg.total ? 'text-green-400' : 'text-amber-400'}`}>
              {reg.aprovados}/{reg.total} aprovados · KB {reg.kb_version}
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {reg.resultados.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs border-b border-gray-800/50 py-1">
                  <span className="truncate pr-2 text-gray-300">{r.id} · {r.pergunta}</span>
                  <span className={`font-bold flex-shrink-0 ${r.passou ? 'text-green-400' : 'text-red-400'}`}>{r.passou ? 'PASS' : `FAIL (${r.obtido || '—'})`}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!reg && <p className="text-gray-500 text-sm">Clique em "Rodar testes" para validar se o RAG recupera a fonte correta nas perguntas de referência.</p>}
      </div>
    </div>
  );
};

const AdminAgente = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [conversas, setConversas] = useState([]);
  const [selecionada, setSelecionada] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [carregandoMsgs, setCarregandoMsgs] = useState(false);
  const [aba, setAba] = useState('conversas');
  const [stats, setStats] = useState(null);
  const [excluindo, setExcluindo] = useState(null);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, { withCredentials: true });
        const [res, st] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/agente/conversas`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/agente/stats`, { withCredentials: true }),
        ]);
        setConversas(res.data);
        setStats(st.data);
        setLoading(false);
      } catch (error) {
        logger.error('Erro/auth em conversas do agente:', error);
        navigate('/admin/login');
      }
    };
    verifyAuth();
    // eslint-disable-next-line
  }, [navigate]);

  const abrirConversa = async (conversa) => {
    setSelecionada(conversa);
    setCarregandoMsgs(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/agente/conversas/${conversa.whatsapp}`, { withCredentials: true });
      setMensagens(res.data);
    } catch (error) {
      logger.error('Erro ao carregar mensagens:', error);
      toast({ title: 'Erro ao carregar a conversa', variant: 'destructive' });
    } finally {
      setCarregandoMsgs(false);
    }
  };

  const excluirConversa = async (conversa, e) => {
    if (e) e.stopPropagation();
    const nome = conversa?.nome || 'este usuário';
    if (!window.confirm(`Excluir a conversa de ${nome} (${formatWA(conversa.whatsapp)})?\n\nTodas as mensagens, sessões e registros deste usuário no Agente IA serão removidos. Esta ação não pode ser desfeita.`)) return;
    setExcluindo(conversa.whatsapp);
    try {
      const res = await axios.delete(`${BACKEND_URL}/api/agente/conversas/${conversa.whatsapp}`, { withCredentials: true });
      setConversas((prev) => prev.filter((c) => c.whatsapp !== conversa.whatsapp));
      if (selecionada?.whatsapp === conversa.whatsapp) { setSelecionada(null); setMensagens([]); }
      try {
        const st = await axios.get(`${BACKEND_URL}/api/agente/stats`, { withCredentials: true });
        setStats(st.data);
      } catch (_) { /* stats é secundário */ }
      toast({ title: 'Conversa excluída', description: `${res.data.mensagens_removidas} mensagens removidas.` });
    } catch (error) {
      logger.error('Erro ao excluir conversa:', error);
      toast({ title: 'Erro ao excluir a conversa', variant: 'destructive' });
    } finally {
      setExcluindo(null);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, { withCredentials: true });
    } catch (e) { /* ignore */ }
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 border-r border-gray-800 transition-all duration-300 flex-shrink-0`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-8">
            {sidebarOpen && <h1 className="text-xl font-bold text-yellow-400">WING ADMIN</h1>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-gray-400 hover:text-white p-2">
              <Menu className="w-6 h-6" />
            </button>
          </div>
          <AdminNav active="agente" navigate={navigate} sidebarOpen={sidebarOpen} onLogout={handleLogout} />
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 p-6 lg:p-8 overflow-hidden flex flex-col">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-white mb-1">Agente Técnico WING</h1>
            <p className="text-gray-400 text-sm">Conversas, feedback e diagnóstico do RAG</p>
          </div>
          <a href="/agente" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm">
            <ExternalLink className="w-4 h-4" /> Abrir página do agente
          </a>
        </div>

        <div className="flex gap-2 mb-5">
          <button onClick={() => setAba('conversas')} data-testid="tab-conversas" className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${aba === 'conversas' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>Conversas</button>
          <button onClick={() => setAba('analytics')} data-testid="tab-analytics" className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${aba === 'analytics' ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>Analytics & Diagnóstico</button>
        </div>

        {aba === 'analytics' ? (
          <div className="flex-1 min-h-0">
            <AgenteAnalyticsPanel />
          </div>
        ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="agente-stats-cards">
              <Card titulo="Total de acessos" valor={stats.total_acessos} />
              <Card titulo={`Alunos (${stats.pct_alunos}%)`} valor={stats.alunos} cor="#4ade80" />
              <Card titulo={`Visitantes (${stats.pct_visitantes}%)`} valor={stats.visitantes} cor="#38bdf8" />
              <Card titulo="Perguntas hoje" valor={stats.perguntas_hoje} />
            </div>
          )}
          <div className="flex-1 flex gap-5 min-h-0">
          {/* Lista de visitantes */}
          <div className="w-full sm:w-80 flex-shrink-0 glass-dark border border-white/10 rounded-xl overflow-y-auto" data-testid="agente-conversas-lista">
            {conversas.length === 0 ? (
              <p className="text-gray-500 text-sm text-center p-8">Nenhuma conversa ainda.</p>
            ) : (
              conversas.map((c) => (
                <div
                  key={c.whatsapp}
                  onClick={() => abrirConversa(c)}
                  data-testid={`conversa-item-${c.whatsapp}`}
                  className={`group w-full text-left px-4 py-3.5 border-b border-gray-800/60 transition-colors cursor-pointer ${
                    selecionada?.whatsapp === c.whatsapp ? 'bg-yellow-500/10 border-l-2 border-l-yellow-500' : 'hover:bg-gray-800/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <UserRound className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                    <span className="text-white font-medium text-sm truncate">{c.nome}</span>
                    {c.is_aluno === true && (
                      <span className="ml-auto flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-500/15 text-green-400" data-testid={`conversa-aluno-${c.whatsapp}`}>Aluno</span>
                    )}
                    {c.is_aluno === false && (
                      <span className="ml-auto flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-400" data-testid={`conversa-naoaluno-${c.whatsapp}`}>Não aluno</span>
                    )}
                    <button
                      onClick={(e) => excluirConversa(c, e)}
                      disabled={excluindo === c.whatsapp}
                      title="Excluir conversa deste usuário"
                      data-testid={`conversa-excluir-${c.whatsapp}`}
                      className="flex-shrink-0 p-1 rounded text-gray-500 hover:text-red-400 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition disabled:opacity-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{formatWA(c.whatsapp)}</span>
                    <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {c.total_perguntas}</span>
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5">
                    {c.ultima_atividade ? new Date(c.ultima_atividade).toLocaleString('pt-BR') : ''}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Thread */}
          <div className="flex-1 glass-dark border border-white/10 rounded-xl overflow-y-auto p-5 hidden sm:block" data-testid="agente-conversa-thread">
            {!selecionada ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-600">
                <Bot className="w-12 h-12 mb-3" />
                <p className="text-sm">Selecione um visitante para ler a conversa</p>
              </div>
            ) : carregandoMsgs ? (
              <p className="text-gray-500 text-sm text-center py-10">Carregando conversa...</p>
            ) : (
              <div className="space-y-4">
                <div className="pb-3 border-b border-gray-800 mb-2">
                  <span className="text-white font-bold">{selecionada.nome}</span>
                  {selecionada.is_aluno === true && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-500/15 text-green-400">Aluno WING</span>
                  )}
                  {selecionada.is_aluno === false && (
                    <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-700/50 text-gray-400">Não aluno</span>
                  )}
                  <span className="text-gray-500 text-sm ml-2">{formatWA(selecionada.whatsapp)}</span>
                  <a
                    href={`https://wa.me/55${selecionada.whatsapp}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="conversa-wa-link"
                    className="ml-3 text-green-400 hover:text-green-300 text-xs font-bold"
                  >
                    Chamar no WhatsApp →
                  </a>
                  <button
                    onClick={(e) => excluirConversa(selecionada, e)}
                    disabled={excluindo === selecionada.whatsapp}
                    data-testid="conversa-excluir-detalhe"
                    className="ml-3 inline-flex items-center gap-1 text-red-400 hover:text-red-300 text-xs font-bold disabled:opacity-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    {excluindo === selecionada.whatsapp ? 'Excluindo...' : 'Excluir conversa'}
                  </button>
                </div>
                {mensagens.map((m, i) => (
                  <div key={m.id || i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                      m.role === 'user'
                        ? 'bg-yellow-500 text-black font-medium rounded-br-sm'
                        : 'bg-gray-900 border border-gray-800 text-gray-300 rounded-bl-sm'
                    }`}>
                      {m.role === 'assistant' ? (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{m.conteudo}</ReactMarkdown>
                      ) : m.conteudo}
                      <div className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-black/50' : 'text-gray-600'}`}>
                        {m.ts ? new Date(m.ts).toLocaleString('pt-BR') : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminAgente;
